import type {
  CooldownRecord,
  CooldownScope,
  EncounterPhase,
  EncounterState,
  IncomingAttackDeclaration,
  ExpirationCondition,
  RecurringDamage,
  TimedEffectRecord,
} from '../domain/encounter.ts';
import { ACTIONS_PER_ROUND } from '../domain/encounter.ts';
import type { RuntimeState } from '../domain/runtime.ts';
import type { Effect } from './effects.ts';
import { advanceTime, cooldownDurationSeconds, ROUND_SECONDS } from './gameTime.ts';

/**
 * Encounter and condition mechanics: rounds, Actions, cooldowns and durations.
 *
 * These operate on the crawler's RuntimeState, which holds the active Effects
 * and cooldowns whether or not a fight is in progress, and an optional
 * EncounterState holding the round and Actions while one is.
 *
 * RULE DCC-ENC-001 - Rules p.17. A round is 10 seconds and each crawler has two
 * Actions in a structured scene.
 *
 * RULE DCC-ENC-002 - Rules p.17. Cooldowns use one of six scopes. Round- and
 * scene-scoped ones end with the encounter; longer ones outlive it.
 *
 * RULE DCC-ENC-003 - Rules p.43. Effects measured in combat rounds end during
 * Combat Step 5: Clean Up.
 */
export { ROUND_SECONDS };

/** Scopes that end when the encounter does. Anything longer survives it. */
export const ENCOUNTER_SCOPED_COOLDOWNS: readonly CooldownScope[] = ['round', 'scene'];

/**
 * RULE DCC-ENC-003 - WHEN an Action may be taken (Rules pp.32-33).
 *
 * The printed round gives crawlers two places to act, and they are not
 * interchangeable. Ordinary Actions - Attack, Cast a Spell, Use a Hotlist Item,
 * Move, Help - belong to Step 4, the Crawler Action Phase. Interrupts belong to
 * Step 2, the Crawler Reaction Phase, which is the window after the Mobs have
 * declared and before their Actions resolve.
 *
 * BOTH SPEND FROM THE SAME POOL. Rules p.33 is explicit: "Spending Actions
 * during this phase counts against the two Actions that each crawler gets every
 * round." There are no reaction points, no per-phase allowance and no second
 * counter, and there is exactly one `actionsRemaining`.
 *
 * The printed exceptions are NOT modelled: surprise's free Action (p.32),
 * the forced crawler-vs-crawler free Evade (p.37) and Intervene's additional
 * legality in Step 4 (p.37) are all deferred, source-backed and recorded.
 */
export type EncounterActionTiming = 'normal' | 'interrupt';

/** The phase each timing category belongs to. */
const TIMING_PHASE: Record<EncounterActionTiming, EncounterPhase> = {
  normal: 'crawler-action',
  interrupt: 'crawler-reaction',
};

const TIMING_PHASE_LABEL: Record<EncounterActionTiming, string> = {
  normal: 'Crawler Action Phase',
  interrupt: 'Crawler Reaction Phase',
};

/**
 * THE ONE ANSWER to "may this Action happen, and can it be afforded?"
 *
 * PURE, and deliberately so. It spends nothing, rolls nothing, expires nothing
 * and returns no runtime. Five services need this question answered before they
 * commit to anything expensive, and five copies of the rule would drift.
 *
 * It does NOT fire `after-next-action`. Staggered, Queasy and Shocked end when
 * the Action they modify COMPLETES, so expiring them here - during a check that
 * might refuse - would end an Effect for an Action that never happened, and
 * would strip the penalty from the very roll it is supposed to apply to.
 * `spendEncounterAction` remains the expiration authority.
 *
 * Returns `null` when the Action may proceed, or the reason it may not.
 */
export function encounterActionProblem(
  runtime: RuntimeState | null,
  actorId: string,
  count: number,
  timing: EncounterActionTiming,
): string | null {
  const encounter = runtime?.encounter ?? null;

  if (!encounter) {
    // Outside a fight there is no phase and no pool. An ordinary Action is
    // simply unrestricted; an INTERRUPT is not, because the Crawler Reaction
    // Phase it belongs to does not exist.
    if (timing === 'interrupt') {
      return 'An Interrupt happens during the Crawler Reaction Phase, and there is no encounter.';
    }
    return null;
  }

  const required = TIMING_PHASE[timing];
  if (encounter.phase !== required) {
    return `That is available during the ${TIMING_PHASE_LABEL[timing]}.`;
  }

  // The FULL count, not one. A non-Hotlisted Spell costs two Actions
  // (DCC-SPELL-002), and asking about one here would let a crawler with a
  // single Action left roll the dice, pay the Mana and start the cooldown
  // before failing at the spend.
  const remaining = encounter.actionsRemaining[actorId] ?? 0;
  if (count > remaining) {
    return `Only ${remaining} Action${remaining === 1 ? '' : 's'} remain this round.`;
  }
  return null;
}

export function spendAction(
  runtime: RuntimeState,
  actorId: string,
  count = 1,
): { ok: true; runtime: RuntimeState } | { ok: false; reason: string } {
  const encounter = runtime.encounter;
  if (!encounter) return { ok: false, reason: 'Actions are only tracked during an encounter.' };

  const remaining = encounter.actionsRemaining[actorId] ?? 0;
  if (count > remaining) {
    return {
      ok: false,
      reason: `Only ${remaining} Action${remaining === 1 ? '' : 's'} remain this round.`,
    };
  }
  return {
    ok: true,
    runtime: {
      ...runtime,
      encounter: {
        ...encounter,
        actionsRemaining: { ...encounter.actionsRemaining, [actorId]: remaining - count },
      },
    },
  };
}

export function startCooldown(
  runtime: RuntimeState,
  cooldown: Omit<CooldownRecord, 'startedOnRound' | 'secondsRemaining'>,
): RuntimeState {
  // Hit or miss, the timer starts (C&C p.22). Restarting replaces the old one.
  const others = runtime.cooldowns.filter((c) => c.sourceId !== cooldown.sourceId);
  const started: CooldownRecord = {
    ...cooldown,
    startedOnRound: runtime.encounter?.round ?? 0,
  };
  // A cooldown measured in time gets its duration now, so `advanceTime` can
  // count it down without knowing what kind of thing started it.
  const seconds = cooldownDurationSeconds(started);
  return {
    ...runtime,
    cooldowns: [...others, seconds === null ? started : { ...started, secondsRemaining: seconds }],
  };
}

export function isOnCooldown(runtime: RuntimeState, sourceId: string): boolean {
  return runtime.cooldowns.some((c) => c.sourceId === sourceId);
}

export function addTimedEffect(runtime: RuntimeState, record: TimedEffectRecord): RuntimeState {
  // Re-applying refreshes rather than stacking a second timer.
  const others = runtime.active.filter((e) => e.effect.id !== record.effect.id);
  return { ...runtime, active: [...others, { ...record }] };
}

export function removeTimedEffect(runtime: RuntimeState, effectId: string): RuntimeState {
  return { ...runtime, active: runtime.active.filter((e) => e.effect.id !== effectId) };
}

/**
 * The Effects currently in play. Handed to `buildEffectiveCharacter` so a Buff
 * or Debuff reaches every calculation through the same path a worn item takes.
 * Available in or out of combat, because most conditions outlive a fight.
 */
export function activeEffects(runtime: RuntimeState): Effect[] {
  return runtime.active.map((record) => record.effect);
}

/** Damage a Debuff owes at Clean Up. The caller rolls and applies it. */
export interface RecurringDamageInstruction {
  effectId: string;
  label: string;
  damage: RecurringDamage;
}

export interface CleanUpResult {
  runtime: RuntimeState;
  expiredEffectIds: string[];
  endedCooldownIds: string[];
  /**
   * RULE DCC-ENC-004 - Rules p.45, Table 8. Returned rather than applied, so
   * the caller puts them through the ordinary mitigation and Health Bar
   * pipeline instead of a second damage path.
   */
  recurringDamage: RecurringDamageInstruction[];
}

/** Combat Step 5: Clean Up, then the next round begins. */
/**
 * Whether this record is still there when the round ends, ten seconds from now.
 *
 * ONLY elapsed-time durations can run out mid-interval. A round-counted
 * duration ends AT a boundary by construction, and every other expiration -
 * end-of-encounter, after-next-action, after-next-check - is triggered by
 * something other than the passage of time and is unaffected here.
 *
 * The shortest elapsed-time condition on a record decides it: a record ends
 * when its FIRST condition is met.
 */
function survivesToRoundBoundary(record: TimedEffectRecord): boolean {
  return record.endsWhen.every(
    (condition) => condition.kind !== 'elapsed-time' || condition.seconds >= ROUND_SECONDS,
  );
}

export function advanceRound(runtime: RuntimeState): CleanUpResult {
  const encounter = runtime.encounter;
  if (!encounter) {
    return { runtime, expiredEffectIds: [], endedCooldownIds: [], recurringDamage: [] };
  }

  const endedCooldownIds: string[] = [];
  const cooldowns = runtime.cooldowns.filter((c) => {
    if (c.scope !== 'round') return true;
    // "Once per round" means usable again next round, so a cooldown started on
    // the round now ending has served its time.
    const finished = c.startedOnRound <= encounter.round;
    if (finished) endedCooldownIds.push(c.sourceId);
    return !finished;
  });

  const expiredEffectIds: string[] = [];
  const recurringDamage: RecurringDamageInstruction[] = [];
  const active: TimedEffectRecord[] = [];

  for (const record of runtime.active) {
    // RULE DCC-COND-008 - damage is owed for the round that is ending,
    // INCLUDING the round in which the Debuff expires: the table says "at the
    // end of each round", and this round did end.
    //
    // But that is only true of a Debuff that LASTED until the boundary. A
    // round-counted one always does - its count runs out AT the end of a round
    // by definition. An elapsed-time one need not: ten seconds pass below, so a
    // record with five seconds left ran out five seconds BEFORE the round
    // ended and was already gone when the damage would have been dealt.
    //
    // Charging it anyway is how a fifteen-second Potion Sickness dealt Poison
    // at a boundary it did not survive to. The threshold is the round itself:
    //
    //   remaining  > 10s -> alive at the boundary, damage owed;
    //   remaining == 10s -> alive THROUGH it, final damage owed, then expires;
    //   remaining  < 10s -> ran out first, nothing owed.
    if (record.recurringDamage && survivesToRoundBoundary(record)) {
      recurringDamage.push({
        effectId: record.effect.id,
        label: record.effect.label,
        damage: record.recurringDamage,
      });
    }

    // Only the round-based condition ticks. Everything else waits for its own
    // printed trigger.
    let expired = false;
    const endsWhen: ExpirationCondition[] = [];
    for (const condition of record.endsWhen) {
      if (condition.kind !== 'after-rounds') {
        endsWhen.push(condition);
        continue;
      }
      const remaining = condition.roundsRemaining - 1;
      if (remaining <= 0) expired = true;
      else endsWhen.push({ kind: 'after-rounds', roundsRemaining: remaining });
    }

    if (expired) expiredEffectIds.push(record.effect.id);
    else active.push({ ...record, endsWhen });
  }

  const actionsRemaining: Record<string, number> = {};
  for (const actorId of Object.keys(encounter.actionsRemaining)) {
    actionsRemaining[actorId] = ACTIONS_PER_ROUND;
  }

  // RULE DCC-TIME-001 - a round is ten seconds (Rules p.17), so ending one
  // makes ten seconds of game time pass. Applied HERE, after the recurring
  // damage above was collected, so a Debuff whose ten minutes run out on this
  // round still owes the damage for the round that just ended: the table says
  // "at the end of each round", and this round did end.
  const passed = advanceTime(
    {
      ...runtime,
      cooldowns,
      active,
      encounter: {
        ...encounter,
        round: encounter.round + 1,
        actionsRemaining,
        // RULE DCC-ENC-002 - Clean Up leads straight into Step 1 of the next
        // round (Rules p.32). There is no phase in between; the crawler is
        // never parked mid-transaction.
        phase: 'mob-declaration' as const,
        // RULE DCC-ENC-006 - last round's threats are not this round's.
        //
        // HERE, in the one authoritative completed-round transition, and NOT on
        // a phase transition: the declarations have to survive the whole walk
        // from Mob Declaration to Crawler Action, because that is the round they
        // describe. Clearing them anywhere else would erase the attacks the
        // crawler is currently reacting to.
        incomingAttacks: [],
      },
    },
    ROUND_SECONDS,
  );

  return {
    runtime: passed.runtime,
    expiredEffectIds: [...expiredEffectIds, ...passed.expiredEffectIds],
    endedCooldownIds: [...endedCooldownIds, ...passed.endedCooldownIds],
    recurringDamage,
  };
}

/**
 * RULE DCC-ENC-002 - moves the round on by ONE OBSERVATIONAL STEP.
 *
 * Steps 1 to 4 are places the fight sits while the table does something the
 * application does not model: the GM declares, the crawler reacts, the Mobs
 * resolve. Advancing between them changes the phase and NOTHING ELSE - not the
 * round, not the Actions, not the clock, not a single Effect or cooldown timer.
 *
 * A PHASE TRANSITION IS NOT A ROUND TRANSITION, and the type enforces it:
 * `crawler-action` has no next phase here. Leaving Step 4 means Clean Up, which
 * expires Effects, resolves recurring damage, regenerates Mana, resets Actions,
 * advances the round and the clock, and commits three records together. Letting
 * this function walk from `crawler-action` back to `mob-declaration` would be a
 * SECOND route to the next round that skipped all of it - a crawler could ride
 * a Debuff forever and never regain an Action. There is one route, and it is
 * `advanceEncounterRound`.
 */
export type PhaseAdvance =
  | { ok: true; runtime: RuntimeState }
  | { ok: false; reason: string };

export function advanceEncounterPhase(runtime: RuntimeState): PhaseAdvance {
  const encounter = runtime.encounter;
  if (!encounter) {
    return { ok: false, reason: 'There is no encounter in progress.' };
  }
  const next = NEXT_PHASE[encounter.phase];
  if (next === null) {
    return {
      ok: false,
      reason:
        'The Crawler Action Phase is finished by completing the round, which runs Clean Up. ' +
        'It cannot be stepped past.',
    };
  }
  return { ok: true, runtime: { ...runtime, encounter: { ...encounter, phase: next } } };
}

/** `null` is not "unknown"; it is "this step is finished by Clean Up". */
const NEXT_PHASE: Record<EncounterPhase, EncounterPhase | null> = {
  'mob-declaration': 'crawler-reaction',
  'crawler-reaction': 'mob-resolution',
  'mob-resolution': 'crawler-action',
  'crawler-action': null,
};

/**
 * RULE DCC-ENC-006 - declared attacks are BOOKKEEPING, and they FREEZE.
 *
 * Adding, editing and removing a declaration is not an Action: it records what
 * the GM announced in Step 1, and the crawler has done nothing. So these cost
 * nothing and go nowhere near `spendEncounterAction`.
 *
 * They are legal ONLY during `mob-declaration`. Once the crawler advances to
 * the Reaction Phase the round's threats are settled, because an attack that
 * could still be edited afterwards is an attack whose Difficulty could change
 * under an Evade result that has already been rolled against it. The freeze is
 * what makes C2's per-attack results mean anything.
 */
export type DeclarationChange =
  | { ok: true; runtime: RuntimeState }
  | { ok: false; reason: string };

function declarationsOpen(runtime: RuntimeState): DeclarationChange | null {
  const encounter = runtime.encounter;
  if (!encounter) return { ok: false, reason: 'There is no encounter in progress.' };
  if (encounter.phase !== 'mob-declaration') {
    return {
      ok: false,
      reason: 'Incoming attacks are entered during Mob Declaration, and are settled after that.',
    };
  }
  return null;
}

const withDeclarations = (
  runtime: RuntimeState,
  incomingAttacks: IncomingAttackDeclaration[],
): RuntimeState => ({
  ...runtime,
  encounter: { ...runtime.encounter!, incomingAttacks },
});

export function addIncomingAttack(
  runtime: RuntimeState,
  attack: IncomingAttackDeclaration,
): DeclarationChange {
  const closed = declarationsOpen(runtime);
  if (closed) return closed;
  const existing = runtime.encounter!.incomingAttacks;
  if (existing.some((entry) => entry.id === attack.id)) {
    return { ok: false, reason: `An incoming attack with id "${attack.id}" is already declared.` };
  }
  // APPENDED, never merged. Three identical spear thrusts are three attacks and
  // will be three separate Evade Checks (Rules p.33); a helper that noticed the
  // duplicate label and combined them would destroy exactly what C2 needs.
  return { ok: true, runtime: withDeclarations(runtime, [...existing, attack]) };
}

export function editIncomingAttack(
  runtime: RuntimeState,
  id: string,
  changes: Omit<Partial<IncomingAttackDeclaration>, 'id'>,
): DeclarationChange {
  const closed = declarationsOpen(runtime);
  if (closed) return closed;
  const existing = runtime.encounter!.incomingAttacks;
  if (!existing.some((entry) => entry.id === id)) {
    return { ok: false, reason: `No incoming attack with id "${id}" is declared.` };
  }
  // The id is not editable: it IS the attack. Changing it would silently make
  // this a different attack rather than an amended one.
  return {
    ok: true,
    runtime: withDeclarations(
      runtime,
      existing.map((entry) => (entry.id === id ? { ...entry, ...changes, id } : entry)),
    ),
  };
}

export function removeIncomingAttack(runtime: RuntimeState, id: string): DeclarationChange {
  const closed = declarationsOpen(runtime);
  if (closed) return closed;
  const existing = runtime.encounter!.incomingAttacks;
  if (!existing.some((entry) => entry.id === id)) {
    return { ok: false, reason: `No incoming attack with id "${id}" is declared.` };
  }
  return {
    ok: true,
    runtime: withDeclarations(
      runtime,
      existing.filter((entry) => entry.id !== id),
    ),
  };
}

export function endsAtEncounterEnd(record: TimedEffectRecord): boolean {
  return record.endsWhen.some((c) => c.kind === 'end-of-encounter' || c.kind === 'after-rounds');
}

export interface EncounterEndResult {
  runtime: RuntimeState;
  expiredEffectIds: string[];
  endedCooldownIds: string[];
}

/**
 * Ends the encounter.
 *
 * Effects and cooldowns that outlive a fight STAY on the runtime state: a
 * Fatigued crawler is still Fatigued when the fighting stops, and a 30-hour
 * cooldown is still running. Only the fight itself goes away.
 */
export function endEncounter(runtime: RuntimeState): EncounterEndResult {
  const expiredEffectIds = runtime.active.filter(endsAtEncounterEnd).map((e) => e.effect.id);
  const expired = new Set(expiredEffectIds);
  const endedCooldownIds = runtime.cooldowns
    .filter((c) => ENCOUNTER_SCOPED_COOLDOWNS.includes(c.scope))
    .map((c) => c.sourceId);
  const ended = new Set(endedCooldownIds);

  return {
    runtime: {
      ...runtime,
      active: runtime.active.filter((r) => !expired.has(r.effect.id)),
      cooldowns: runtime.cooldowns.filter((c) => !ended.has(c.sourceId)),
      encounter: null,
    },
    expiredEffectIds,
    endedCooldownIds,
  };
}

/**
 * RULE DCC-COND-002 - Stunned "Once you make a Check"; Staggered, Queasy and
 * Shocked "At the end of the next Action you take" (Rules pp.45-46).
 * The caller reports that the trigger happened; nothing here watches for it.
 */
export function resolveTrigger(
  runtime: RuntimeState,
  trigger: 'after-next-check' | 'after-next-action',
): { runtime: RuntimeState; expiredEffectIds: string[] } {
  const expiredEffectIds = runtime.active
    .filter((r) => r.endsWhen.some((c) => c.kind === trigger))
    .map((r) => r.effect.id);
  const expired = new Set(expiredEffectIds);
  return {
    runtime: { ...runtime, active: runtime.active.filter((r) => !expired.has(r.effect.id)) },
    expiredEffectIds,
  };
}

/**
 * Ends an Effect through one of its printed non-round conditions. Refuses when
 * the Effect does not carry that condition, so an interface cannot end Burned
 * by "curing" it when the table gives no cure.
 */
export function endByCondition(
  runtime: RuntimeState,
  effectId: string,
  kind: ExpirationCondition['kind'],
): { ok: true; runtime: RuntimeState } | { ok: false; reason: string } {
  const record = runtime.active.find((r) => r.effect.id === effectId);
  if (!record) return { ok: false, reason: `No active Effect with id "${effectId}".` };
  if (!record.endsWhen.some((c) => c.kind === kind)) {
    return {
      ok: false,
      reason: `${record.effect.label} does not end that way. It ends: ${describeExpiration(record).join('; ')}.`,
    };
  }
  return { ok: true, runtime: removeTimedEffect(runtime, effectId) };
}

/**
 * RULE DCC-COND-010 - conditions that end with a rest.
 *
 * Rest tiers are CUMULATIVE by duration: a Full Day's Rest is 30 hours and so
 * contains the 8 of a long rest, which contains the 2 of a short one. Rules p.43
 * supports this, describing a full day's rest as recovering long-term injuries
 * and "also" full Health and Mana - everything a long rest does, plus more.
 * See docs/RULES_CONFLICTS.md RESOLVED-006.
 */
const REST_RANK: Record<'short' | 'long' | 'full-day', number> = {
  short: 1,
  long: 2,
  'full-day': 3,
};

export function endsAtRest(
  record: TimedEffectRecord,
  rest: 'short' | 'long' | 'full-day',
): boolean {
  return record.endsWhen.some(
    (c) => c.kind === 'end-of-rest' && REST_RANK[c.rest] <= REST_RANK[rest],
  );
}

export function applyRestToConditions(
  runtime: RuntimeState,
  rest: 'short' | 'long' | 'full-day',
): { runtime: RuntimeState; expiredEffectIds: string[] } {
  const expiredEffectIds = runtime.active.filter((r) => endsAtRest(r, rest)).map((r) => r.effect.id);
  const expired = new Set(expiredEffectIds);
  return {
    runtime: { ...runtime, active: runtime.active.filter((r) => !expired.has(r.effect.id)) },
    expiredEffectIds,
  };
}

/** Plain-language lines for the interface, one per printed condition. */
export function describeExpiration(record: TimedEffectRecord): string[] {
  return record.endsWhen.map((c) => {
    switch (c.kind) {
      case 'after-rounds':
        return `${c.roundsRemaining} round${c.roundsRemaining === 1 ? '' : 's'} left`;
      case 'end-of-encounter':
        return 'end of combat';
      case 'elapsed-time':
        return describeSeconds(c.seconds);
      case 'after-next-check':
        return 'once you make a Check';
      case 'after-next-action':
        return 'at the end of your next Action';
      case 'cured':
        return `cured with ${c.treatment}`;
      case 'end-of-rest':
        return `end of a ${c.rest} rest`;
      case 'check-to-end':
        return c.description;
      case 'released':
        return 'when released';
      case 'healed':
        return `healed at least ${c.minimumHealthBarSlots} Health Bar slot${c.minimumHealthBarSlots === 1 ? '' : 's'}`;
      case 'damage-taken':
        return `you take at least ${c.minimumHealthBarSlots} Health Bar slot${c.minimumHealthBarSlots === 1 ? '' : 's'} of damage`;
      case 'action-to-end':
        return c.description;
      case 'manual':
        return c.description ?? 'ended by the GM';
    }
  });
}

function describeSeconds(seconds: number): string {
  if (seconds < 60) return `${seconds} seconds`;
  if (seconds % 3600 === 0) {
    const hours = seconds / 3600;
    return `${hours} hour${hours === 1 ? '' : 's'}`;
  }
  const minutes = seconds / 60;
  return `${minutes} minute${minutes === 1 ? '' : 's'}`;
}

/** Starts an encounter on an existing runtime state, keeping active conditions. */
export function beginEncounter(runtime: RuntimeState, encounter: EncounterState): RuntimeState {
  return { ...runtime, encounter };
}
